import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Reservation, ReservationStatus } from '@reims-local/types';
import { reservationApi } from '../lib/api';

export function MyReservations() {
  const [rows, setRows] = useState<Array<{ reservation: Reservation; shop: { id: string; name: string; slug: string; address: string | null; phone: string | null } }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    reservationApi
      .listMine()
      .then((res) => setRows(res.data))
      .catch((err) => setError((err as Error).message === 'not_authenticated' ? 'Connectez-vous pour voir vos réservations.' : (err as Error).message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="container">
      <h2>Mes réservations</h2>
      {error && <div className="alert alert-error">{error}</div>}
      {loading ? (
        <p>Chargement…</p>
      ) : rows.length === 0 ? (
        <p className="muted">Vous n’avez pas encore de réservation.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rows.map(({ reservation, shop }) => (
            <Link to={`/mes-reservations/${reservation.id}`} key={reservation.id} style={{ color: 'inherit' }}>
              <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div><strong>{reservation.reservation_number}</strong> — {shop.name}</div>
                    <div className="muted" style={{ fontSize: 13 }}>
                      {formatDate(reservation.date)} à {formatTime(reservation.time)} · {reservation.covers} p.
                    </div>
                  </div>
                  <span className={`badge ${statusClass(reservation.status as ReservationStatus)}`}>{statusLabel(reservation.status as ReservationStatus)}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </main>
  );
}

export function ReservationTrack({ reservationId }: { reservationId: string }) {
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof reservationApi.get>>['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  async function load() {
    try {
      const res = await reservationApi.get(reservationId);
      setDetail(res.data);
      setError(null);
    } catch (err) {
      setError((err as Error).message === 'not_authenticated' ? 'Connectez-vous pour voir cette réservation.' : 'Introuvable.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [reservationId]);

  async function cancel() {
    if (!confirm('Annuler cette réservation ?')) return;
    setCancelling(true);
    try {
      await reservationApi.cancel(reservationId);
      await load();
    } catch (err) {
      const code = (err as Error).message;
      alert(code === 'deadline_passed' ? 'Trop tard pour annuler (moins de 2h avant).' : code);
    } finally {
      setCancelling(false);
    }
  }

  if (loading) return <main className="container"><p>Chargement…</p></main>;
  if (error) return <main className="container"><div className="alert alert-error">{error}</div></main>;
  if (!detail) return null;
  const { reservation, shop } = detail;
  const status = reservation.status as ReservationStatus;
  const timeMs = new Date(reservation.time).getTime();
  const cancellable = (status === 'confirmed' || status === 'pending') && timeMs - Date.now() > 2 * 3600_000;

  return (
    <main className="container">
      <Link to="/mes-reservations">← Mes réservations</Link>
      <h2 style={{ marginTop: 12 }}>Réservation {reservation.reservation_number}</h2>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <strong>Statut</strong>
          <span className={`badge ${statusClass(status)}`}>{statusLabel(status)}</span>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>{shop?.name}</h3>
        {shop?.address && <p className="muted" style={{ margin: 0 }}>📍 {shop.address}</p>}
        {shop?.phone && <p className="muted" style={{ margin: 0 }}>📞 {shop.phone}</p>}
        <p style={{ marginTop: 12 }}>
          <strong>{formatDateLong(reservation.date)} à {formatTime(reservation.time)}</strong>
          <br />
          {reservation.covers} couvert{(reservation.covers ?? 0) > 1 ? 's' : ''}
        </p>
        {reservation.special_request && (
          <>
            <div className="muted" style={{ marginTop: 16 }}>Votre demande</div>
            <div>{reservation.special_request}</div>
          </>
        )}
      </div>

      {cancellable && (
        <button className="ghost" onClick={cancel} disabled={cancelling} style={{ width: '100%' }}>
          {cancelling ? 'Annulation…' : 'Annuler cette réservation'}
        </button>
      )}
    </main>
  );
}

function statusClass(s: ReservationStatus): string {
  if (s === 'confirmed') return 'green';
  if (s === 'cancelled' || s === 'no_show') return 'red';
  return 'orange';
}
function statusLabel(s: ReservationStatus): string {
  const m: Record<ReservationStatus, string> = {
    pending: 'En attente', confirmed: 'Confirmée', cancelled: 'Annulée', no_show: 'No-show',
  };
  return m[s];
}
function formatDate(date: string): string {
  try {
    return new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', weekday: 'short', day: 'numeric', month: 'short' })
      .format(new Date(`${date}T12:00:00.000Z`));
  } catch { return date; }
}
function formatDateLong(date: string): string {
  try {
    return new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
      .format(new Date(`${date}T12:00:00.000Z`));
  } catch { return date; }
}
function formatTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
  } catch { return iso; }
}
